import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import Landing from "./pages/Landing";
import Gallery from "./pages/Gallery";
import Packages from "./pages/Packages";
import Shop from "./pages/Shop";
import Credits from "./pages/Credits";
import HowItWorks from "./pages/HowItWorks";
import PromptBuilder from "./pages/PromptBuilder";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import News from "./pages/News";
import GoldChat from "./pages/GoldChat";
import Referrals from "./pages/Referrals";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Contact from "./pages/Contact";
import ForgotPassword from "./pages/ForgotPassword";
import Invoices from "./pages/Invoices";
import Profile from "./pages/Profile";
import ChatBot from "./components/ChatBot";
import CookieConsent from "./components/CookieConsent";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { NotificationProvider } from "./context/NotificationContext";

// Import i18n configuration (must be imported before any component that uses translations)
import './i18n/config';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Auto-detect language from browser/IP
function LanguageDetector() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Check if user has already set a language preference
    const savedLang = localStorage.getItem('ella-gold-language');
    if (savedLang) return;
    
    // Detect from browser/IP
    axios.get(`${API}/detect-language`)
      .then(res => {
        const detectedLang = res.data?.detected_language || 'en';
        if (detectedLang !== i18n.language) {
          i18n.changeLanguage(detectedLang);
          localStorage.setItem('ella-gold-language', detectedLang);
        }
      })
      .catch(() => {
        // Fallback to browser language
        const browserLang = navigator.language?.split('-')[0] || 'en';
        const supportedLangs = ['en', 'nl', 'de', 'fr', 'es', 'it', 'ru', 'ar'];
        if (supportedLangs.includes(browserLang) && browserLang !== i18n.language) {
          i18n.changeLanguage(browserLang);
        }
      });
  }, [i18n]);
  
  return null;
}

function AppContent() {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith('nl') ? 'nl' : 'en';
  
  return (
    <>
      <ScrollToTop />
      <LanguageDetector />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/builder" element={<PromptBuilder />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/news" element={<News />} />
        <Route path="/chat" element={<GoldChat />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/profile" element={<Profile />} />
      </Routes>
      <ChatBot language={language} />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App noise-overlay">
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
              <Toaster 
                position="top-right" 
                toastOptions={{
                  style: {
                    background: '#0A0A0A',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#F8F8F8',
                  },
                }}
              />
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
